import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { Button } from "@shared/ui/button";
import { Dialog, Notice } from "@shared/ui/bits";
import { Spinner } from "@shared/ui/spinner";
import { IconDown, IconWarning } from "@shared/ui/icons";
import {
  currentRestartBlockers,
  restartLifecycleVersion,
  subscribeRestartLifecycle,
} from "@shared/lib/desktop";
import {
  recoveryMessage,
  updater,
  updateBusy,
  type UpdateSnapshot,
} from "../model/controller";

function percent(state: UpdateSnapshot): number | null {
  if (!state.contentLength || state.contentLength <= 0) return null;
  return Math.min(100, Math.round((state.downloaded / state.contentLength) * 100));
}

function megabytes(bytes: number): string {
  const value = bytes / (1024 * 1024);
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} МБ`;
}

function sentence(text: string): string {
  const trimmed = text.trim();
  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function stageLabel(state: UpdateSnapshot): string {
  switch (state.phase) {
    case "revalidating":
      return "Проверяю, что релиз ещё актуален…";
    case "downloading":
      return state.contentLength
        ? `${megabytes(state.downloaded)} из ${megabytes(state.contentLength)}`
        : state.downloaded > 0
          ? `${megabytes(state.downloaded)} загружено`
          : "Начинаю загрузку…";
    case "verifying":
      return "Проверяю подпись и актуальность пакета…";
    case "staging":
      return "Подготавливаю безопасный запуск установщика…";
    case "preparing":
      return "Сохраняю черновики и завершаю текущие действия…";
    case "installing":
      return "Передаю пакет системному установщику…";
    case "restarting":
      return "Перезапускаю Airel…";
    default:
      return "Проверяю обновление…";
  }
}

function HealthProbe({ state }: { state: UpdateSnapshot }) {
  const failed = state.launchMode === "failed";
  const version = state.healthProbe?.targetVersion;

  useEffect(
    () =>
      scheduleHealthProbeAck(
        state.launchMode === "healthProbe" && !!state.healthProbe,
        () => void updater.completeHealthProbe().catch(() => {}),
      ),
    [state.healthProbe, state.launchMode],
  );

  return (
    <main className="update-boot" aria-busy={!failed}>
      <div className="update-boot-card" role="status" aria-live="polite">
        <span className="brand-mark brand-mark-auth" aria-hidden="true" />
        <span className="update-boot-brand">AIREL</span>
        <h1>{failed ? "Нужен повторный запуск" : `Проверяем Airel ${version ?? ""}`.trim()}</h1>
        <p>
          {state.healthProbeError ??
            (state.launchMode === "detecting"
              ? "Проверяем состояние обновления перед запуском приложения."
              : "Новая версия запустилась. Завершаем безопасную установку.")}
        </p>
        {!failed && <Spinner size={22} />}
      </div>
    </main>
  );
}

function Progress({ state }: { state: UpdateSnapshot }) {
  const value = percent(state);
  const downloading = state.phase === "downloading";

  return (
    <div className="update-progress-wrap">
      {downloading && (
        <div
          className={`update-progress ${value === null ? "unknown" : ""}`}
          role="progressbar"
          aria-label="Загрузка обновления"
          aria-valuemin={value === null ? undefined : 0}
          aria-valuemax={value === null ? undefined : 100}
          aria-valuenow={value ?? undefined}
          aria-valuetext={stageLabel(state)}
        >
          <span style={value === null ? undefined : { width: `${value}%` }} />
        </div>
      )}
      <span
        className="update-progress-label"
        role={downloading ? undefined : "status"}
        aria-live={downloading ? undefined : "polite"}
      >
        {downloading && value !== null ? `${value}% · ` : ""}
        {stageLabel(state)}
      </span>
    </div>
  );
}

function Boot({ state }: { state: UpdateSnapshot }) {
  const installing = updateBusy(state.phase);

  return (
    <main className="update-boot" aria-busy="true">
      {state.showBootMessage && (
        <div className="update-boot-card" role="status" aria-live="polite">
          <span className="brand-mark brand-mark-auth" aria-hidden="true" />
          <span className="update-boot-brand">AIREL</span>
          <h1>{installing ? `Обновляем до ${state.update?.version ?? "новой версии"}` : "Проверяем обновление"}</h1>
          <p>
            {installing
              ? "Не закрывайте приложение. После установки Airel откроется снова."
              : "Это займёт пару секунд."}
          </p>
          {installing ? <Progress state={state} /> : <Spinner size={22} />}
        </div>
      )}
    </main>
  );
}

function UpdateBanner({ state }: { state: UpdateSnapshot }) {
  const value = percent(state);
  const installing = updateBusy(state.phase);
  const recovery = state.recovery;

  return (
    <div
      className={`update-banner ${recovery || state.installError || state.update?.mandatory ? "bad" : ""}`}
      role="status"
    >
      {installing ? (
        <Spinner size={14} />
      ) : recovery || state.installError || state.blockingMessage ? (
        <IconWarning size={14} />
      ) : (
        <IconDown size={14} />
      )}
      <span className="grow ellipsis">
        {installing
          ? `${stageLabel(state)}${state.phase === "downloading" && value !== null ? ` · ${value}%` : ""}`
          : recovery
            ? recoveryMessage(recovery)
            : state.installError
              ? "Обновление не установилось. Текущая версия продолжает работать."
              : state.blockingMessage
                ? "Обновление загружено. Завершите текущие действия."
                : state.update?.mandatory
                  ? `Нужно обновить Airel до ${state.update.version}`
                  : `Доступен Airel ${state.update?.version ?? ""}`}
      </span>
      <Button type="button" size="sm" variant="secondary" disabled={installing} onClick={() => updater.openDialog()}>
        {recovery
          ? recovery.status === "recoveryPending"
            ? "Что делать"
            : recovery.retryable && state.update
              ? "Повторить"
              : "Подробнее"
          : state.installError
            ? "Повторить"
            : state.blockingMessage
              ? "Продолжить"
              : "Обновить"}
      </Button>
    </div>
  );
}

/** Слот ставится сразу под titlebar каждого корневого экрана. */
export function UpdateBannerSlot() {
  const state = useSyncExternalStore(updater.subscribe, updater.getSnapshot, updater.getSnapshot);
  return (state.update || state.recovery) && !state.booting ? <UpdateBanner state={state} /> : null;
}

function UpdateDialog({ state }: { state: UpdateSnapshot }) {
  useSyncExternalStore(subscribeRestartLifecycle, restartLifecycleVersion, restartLifecycleVersion);
  const blockers = currentRestartBlockers();
  const waiting = blockers.filter((blocker) => blocker.kind === "wait");
  const confirmed = blockers.filter((blocker) => blocker.kind === "confirm");
  const installing = updateBusy(state.phase);
  const recovery = state.recovery;
  const canDefer = !state.update?.mandatory || blockers.length > 0 || !!state.installError;

  const actions = recovery ? (
    <>
      {recovery.status === "recoveryPending" ? (
        <Button type="button" size="sm" variant="secondary" onClick={() => updater.closeDialog()}>
          Понятно
        </Button>
      ) : (
        <>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => void updater.dismissRecovery().catch(() => {})}
          >
            Скрыть сообщение
          </Button>
          {recovery.retryable && state.update && (
            <Button
              type="button"
              size="sm"
              onClick={() => void updater.installRuntime().catch(() => {})}
            >
              Повторить обновление
            </Button>
          )}
        </>
      )}
    </>
  ) : (
    <>
      {canDefer && (
        <Button type="button" size="sm" variant="secondary" disabled={installing} onClick={() => updater.closeDialog()}>
          {state.update?.mandatory
            ? blockers.length > 0
              ? "Вернуться и завершить"
              : "Продолжить работу"
            : "Позже"}
        </Button>
      )}
      <Button
        type="button"
        size="sm"
        disabled={installing}
        onClick={() => void updater.installRuntime().catch(() => {})}
      >
        {installing
          ? state.phase === "downloading"
            ? "Загружаю…"
            : state.phase === "verifying"
              ? "Проверяю…"
              : state.phase === "staging"
                ? "Подготавливаю…"
                : state.phase === "preparing"
                  ? "Подготавливаю…"
                  : state.phase === "restarting"
                    ? "Перезапускаю…"
                    : "Устанавливаю…"
          : waiting.length > 0
            ? "Скачать обновление"
            : confirmed.length > 0
              ? "Завершить и обновить"
              : "Обновить сейчас"}
      </Button>
    </>
  );

  return (
    <Dialog
      title={
        recovery
          ? recovery.status === "canceled"
            ? "Установка отменена"
            : recovery.status === "rolledBack"
              ? "Предыдущая версия восстановлена"
              : "Восстановление ещё не завершено"
          : `Обновление Airel ${state.update?.version ?? ""}`
      }
      onClose={() => canDefer && updater.closeDialog()}
      actions={actions}
    >
      <div className="update-dialog-body">
        {state.update?.notes && <p className="update-notes">{state.update.notes}</p>}

        {recovery?.status === "canceled" && (
          <Notice kind="warn" title="Windows не получила подтверждение">
            Вы отменили системный запрос на установку Airel {recovery.targetVersion}. Ничего не сломано:
            текущая версия продолжает работать. Повторите обновление, когда будете готовы подтвердить запрос Windows.
          </Notice>
        )}

        {recovery?.status === "rolledBack" && (
          <Notice kind="bad" title="Windows сохранила рабочее приложение">
            Установка Airel {recovery.targetVersion} завершилась ошибкой. Windows автоматически вернула предыдущую
            рабочую версию, поэтому можно продолжить работу. {recovery.retryable
              ? "Обновление можно повторить."
              : "Дождитесь следующей версии или обратитесь в поддержку."}
          </Notice>
        )}

        {recovery?.status === "recoveryPending" && (
          <Notice kind="bad" title="Не повторяйте установку">
            Результат установки Airel {recovery.targetVersion} пока не подтверждён. Полностью закройте Airel и
            запустите его снова. Если это сообщение останется, передайте в поддержку версию и время ошибки.
          </Notice>
        )}

        {state.recoveryActionError && (
          <Notice kind="bad" title="Не удалось сохранить действие">
            {sentence(state.recoveryActionError)} Текущая версия продолжает работать.
          </Notice>
        )}

        {state.update?.mandatory && !state.installError && !recovery && (
          <Notice kind="bad" title="Эта версия обязательна">
            Она нужна для совместимости и безопасной работы Airel. Завершите текущие действия и обновитесь.
          </Notice>
        )}

        {state.installError && !recovery && (
          <Notice kind="bad" title="Установить обновление не удалось">
            {sentence(state.installError)} Можно продолжить работу и повторить позже.
          </Notice>
        )}

        {state.blockingMessage && !recovery && (
          <Notice kind="warn" title="Пакет загружен и проверен">
            {sentence(state.blockingMessage)} После завершения повторите установку — заново скачивать пакет не придётся.
          </Notice>
        )}

        {!recovery && waiting.length > 0 && (
          <Notice kind="warn" title="Сначала дождитесь завершения">
            {waiting.map((blocker) => (
              <span className="update-blocker" key={blocker.id}>
                <strong>{blocker.title}</strong> — {blocker.detail}
              </span>
            ))}
          </Notice>
        )}

        {!recovery && waiting.length === 0 && confirmed.length > 0 && (
          <Notice kind="warn" title="Приложение будет перезапущено">
            {confirmed.map((blocker) => (
              <span className="update-blocker" key={blocker.id}>
                <strong>{blocker.title}</strong> — {blocker.detail}
              </span>
            ))}
          </Notice>
        )}

        {!recovery && installing && <Progress state={state} />}
        {!recovery && !installing && blockers.length === 0 && (
          <p className="update-restart-note">
            Airel сохранит черновики и откроется снова после установки. Windows может завершать настройку ещё около
            минуты после закрытия установщика.
          </p>
        )}
      </div>
    </Dialog>
  );
}

type RequestFrame = (callback: FrameRequestCallback) => number;
type CancelFrame = (handle: number) => void;

/** ACK разрешён только health-probe ветке и только после двух нарисованных кадров. */
export function scheduleHealthProbeAck(
  enabled: boolean,
  acknowledge: () => void,
  requestFrame: RequestFrame = requestAnimationFrame,
  cancelFrame: CancelFrame = cancelAnimationFrame,
): () => void {
  if (!enabled) return () => {};

  let active = true;
  let secondFrame = 0;
  const firstFrame = requestFrame(() => {
    secondFrame = requestFrame(() => {
      if (active) acknowledge();
    });
  });

  return () => {
    active = false;
    cancelFrame(firstFrame);
    if (secondFrame) cancelFrame(secondFrame);
  };
}

export function UpdateBoundary({
  children,
  renderCoreSurface = (content) => content,
}: {
  children: ReactNode;
  renderCoreSurface?: (content: ReactNode) => ReactNode;
}) {
  const state = useSyncExternalStore(updater.subscribe, updater.getSnapshot, updater.getSnapshot);
  const lifecycle = useSyncExternalStore(
    subscribeRestartLifecycle,
    restartLifecycleVersion,
    restartLifecycleVersion,
  );

  useEffect(() => {
    if (state.launchMode === "detecting") void updater.detectBootMode();
  }, [state.launchMode]);

  useEffect(() => {
    if (state.launchMode !== "normal") return;
    updater.start();
    return updater.schedule();
  }, [state.launchMode]);

  useEffect(() => {
    if (
      !state.booting &&
      state.phase === "available" &&
      state.update?.mandatory &&
      !state.installError &&
      !state.dialog &&
      currentRestartBlockers().length === 0
    ) {
      updater.openDialog();
    }
  }, [lifecycle, state.booting, state.dialog, state.installError, state.phase, state.update]);

  if (state.desktop && state.launchMode === "healthProbe") {
    return renderCoreSurface(<HealthProbe state={state} />);
  }
  if (state.desktop && state.launchMode !== "normal") return <HealthProbe state={state} />;
  if (state.booting) return renderCoreSurface(<Boot state={state} />);

  return (
    <div className="update-root">
      <div className="update-app">{children}</div>
      {state.dialog && (state.update || state.recovery) && <UpdateDialog state={state} />}
    </div>
  );
}
